import { motion } from 'motion/react'
import {
  AlertTriangle,
  CalendarDays,
  ExternalLink,
  FileText,
  Github,
  Pencil,
  type LucideIcon,
} from 'lucide-react'
import type { MoodboardItem, MoodboardPolarity } from '@backend/lib/db/schema/moodboard'
import { useCanvasStore } from '../canvas-store'
import { useDragInteraction } from '../use-drag-interaction'
import { SNAP_CURVE } from '../motion-tokens'
import { ItemInfoBadge } from './item-info-badge'
import { ResizeHandle } from './resize-handle'

type ConnectorItem = Extract<MoodboardItem, { type: 'connector' }>

/**
 * Whether this connector can currently be read, derived live rather than stored. A connection
 * can go away without CASSI being involved (disconnected here, or the app uninstalled / a repo
 * dropped from the installation on GitHub itself), so a flag written at disconnect time would
 * miss most of the ways this breaks and then lie. Deriving it also means reconnecting the same
 * account heals every connector with no bookkeeping.
 */
export type ConnectorStatus =
  | 'ok'
  /** Still loading, or we can't tell (Notion targets aren't cheaply verifiable). */
  | 'unknown'
  /** The brand has no connection for this provider at all. */
  | 'no-connection'
  /** Connected, but this target isn't in what the connection can reach. */
  | 'not-covered'
  /** Connected, but the provider no longer recognises the installation. */
  | 'connection-invalid'

function CardAction({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      className="bg-card/95 border-border/60 text-muted-foreground hover:text-foreground hover:bg-hover rounded-full border p-1.5 shadow-sm backdrop-blur transition-colors">
      {children}
    </button>
  )
}

function splitRepo(target: string): [string, string] | [null, null] {
  const slash = target.indexOf('/')
  if (slash <= 0 || slash === target.length - 1) return [null, null]
  return [target.slice(0, slash), target.slice(slash + 1)]
}

const STATUS_COPY: Record<Exclude<ConnectorStatus, 'ok' | 'unknown'>, string> = {
  'no-connection': 'Not connected. Reconnect in Settings → Content Connections.',
  'not-covered': 'This account can no longer read this target.',
  'connection-invalid': 'The connection is no longer valid. Reconnect it in Settings.',
}

/** Where this target lives in the provider's own UI. Notion accepts a bare id and redirects.
 *  Google Calendar has no stable per-calendar URL, so it opens the calendar app itself. */
function connectorHref(item: ConnectorItem): string {
  switch (item.provider) {
    case 'github':
      return `https://github.com/${item.target}`
    case 'notion':
      return `https://www.notion.so/${item.target.replace(/-/g, '')}`
    case 'google_calendar':
      return 'https://calendar.google.com/calendar/r'
  }
}

const PROVIDER_COPY: Record<
  ConnectorItem['provider'],
  { icon: LucideIcon; title: string; noun: string; info: string }
> = {
  github: {
    icon: Github,
    title: 'GitHub connector',
    noun: 'repository',
    info: 'GitHub repository the agent may read (releases, commits, PRs, files).',
  },
  notion: {
    icon: FileText,
    title: 'Notion connector',
    noun: 'page',
    info: 'Notion page/database the agent may read.',
  },
  google_calendar: {
    icon: CalendarDays,
    title: 'Calendar connector',
    noun: 'calendar',
    info: 'Google Calendar the agent may read for upcoming events. Attendees and call links are never read.',
  },
}

type Props = {
  item: ConnectorItem
  status?: ConnectorStatus
  /** Re-opens the picker over this card so the target can be swapped in place. */
  onReselect: () => void
  allItems: MoodboardItem[]
  onMove: (position: { x: number; y: number }) => Promise<void> | void
  onResize: (size: { w: number; h: number }) => Promise<void> | void
  onMetaChange: (patch: {
    polarity?: MoodboardPolarity | undefined
    annotation?: string | undefined
  }) => Promise<void> | void
}

// A live connector source on the canvas (GitHub, Notion or Google Calendar). Not visual reference material —
// it tells the agent it may read this repo. Kept deliberately compact.
export function ConnectorCard({
  item,
  status = 'unknown',
  onReselect,
  allItems,
  onMove,
  onResize,
  onMetaChange,
}: Props) {
  const override = useCanvasStore((s) => s.dragOverrides[item.id])
  const resizeOverride = useCanvasStore((s) => s.resizeOverrides[item.id])
  const isDragging = !!override
  const position = override?.position ?? item.position
  const size = resizeOverride?.size ?? item.size
  const isSelected = useCanvasStore((s) => s.selectedIds.has(item.id))
  const drag = useDragInteraction({
    itemId: item.id,
    basePosition: item.position,
    linkedItems: allItems,
    onCommit: onMove,
    onSelect: (e) => {
      useCanvasStore.getState().selectFromPointer(item.id, e.shiftKey)
    },
  })

  const isGithub = item.provider === 'github'
  const copy = PROVIDER_COPY[item.provider]
  const ProviderIcon = copy.icon
  const isBroken = status !== 'ok' && status !== 'unknown'
  const brokenReason = isBroken ? STATUS_COPY[status] : null
  // A GitHub target is "owner/name". Splitting it lets the repo carry the weight and the owner
  // recede, which is how you actually read one at a glance on a crowded board.
  const [owner, repo] = isGithub ? splitRepo(item.target) : [null, null]
  const label = item.label || item.target

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.24, ease: SNAP_CURVE }}
      className="group absolute"
      style={{ left: position.x, top: position.y, width: size.w, height: size.h }}>
      <div
        {...drag}
        className={[
          'bg-card text-foreground relative flex h-full w-full flex-col justify-center gap-1 overflow-hidden rounded-lg px-3 shadow-sm ring-1 transition-shadow',
          isBroken ? 'ring-destructive/60' : 'ring-border/60',
          isDragging ? 'cursor-grabbing shadow-lg' : 'cursor-grab',
          isSelected ? 'ring-primary ring-offset-background ring-2 ring-offset-2' : '',
        ].join(' ')}>
        <div className="flex items-center gap-2">
          {isBroken ? (
            <AlertTriangle size={16} className="text-destructive shrink-0" />
          ) : (
            <ProviderIcon size={16} className="text-muted-foreground shrink-0" />
          )}
          <p
            className={[
              'text-[10px] font-medium uppercase tracking-wide',
              isBroken ? 'text-destructive' : 'text-muted-foreground',
            ].join(' ')}>
            {copy.title}
          </p>
        </div>
        <p className="truncate text-sm">
          {owner && repo ? (
            <>
              <span className="text-muted-foreground">{owner}/</span>
              <span className="text-foreground font-medium">{repo}</span>
            </>
          ) : (
            <span className="text-foreground font-medium">{label}</span>
          )}
        </p>
        {brokenReason ? (
          <p className="text-destructive text-xs leading-snug">{brokenReason}</p>
        ) : (
          <p className="text-muted-foreground text-xs">
            Read fresh at generation, never in the brief.
          </p>
        )}

        {/* Floating cluster, hover-revealed so the resting card stays quiet. pointer-down is
            stopped on each so a click doesn't start a canvas drag. */}
        <div className="absolute bottom-2 right-2 z-10 flex items-center gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
          <CardAction
            label={`Open ${copy.noun} in a new tab`}
            onClick={() => window.open(connectorHref(item), '_blank', 'noopener,noreferrer')}>
            <ExternalLink size={12} />
          </CardAction>
          <CardAction label="Change target" onClick={onReselect}>
            <Pencil size={12} />
          </CardAction>
        </div>

        <ItemInfoBadge
          polarity={item.polarity}
          annotation={item.annotation}
          onChange={onMetaChange}>
          <p className="text-foreground truncate font-medium">{label}</p>
          <p>{copy.info}</p>
        </ItemInfoBadge>
        <ResizeHandle itemId={item.id} baseSize={item.size} onCommit={onResize} />
      </div>
    </motion.div>
  )
}
